import { useState, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { TrendingUp, ArrowDown } from 'lucide-react';
import { ProviderTimelinePoint, ProviderSummary } from '@/api/queriesService';
import { LlmProvider, LLM_PROVIDER_LOGOS, LLM_PROVIDER_LABELS, ALL_LLM_PROVIDERS } from '@/types/llm-provider';

type MetricType = 'rate' | 'count';

interface ProviderPerformanceChartProps {
  timeline: ProviderTimelinePoint[];
  providerSummaries: ProviderSummary[];
}

const PROVIDER_COLORS: Record<LlmProvider, string> = {
  [LlmProvider.ChatGPT]: '#10a37f',
  [LlmProvider.Claude]: '#d97706',
  [LlmProvider.Gemini]: '#4285f4',
  [LlmProvider.Perplexity]: '#20b2aa',
  [LlmProvider.Grok]: '#1da1f2',
  [LlmProvider.DeepSeek]: '#7c3aed',
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const transformTimelineData = (
  timeline: ProviderTimelinePoint[],
  metricType: MetricType
): Record<string, string | number>[] => {
  const dateMap = new Map<string, Record<string, string | number>>();

  for (const point of timeline) {
    if (!dateMap.has(point.date)) {
      dateMap.set(point.date, { date: point.date });
    }
    const entry = dateMap.get(point.date)!;
    const key = metricType === 'rate' ? `${point.provider}_rate` : `${point.provider}_count`;
    entry[key] = metricType === 'rate' ? point.mentionRate : point.mentions;
  }

  return Array.from(dateMap.values()).sort((a, b) =>
    new Date(a.date as string).getTime() - new Date(b.date as string).getTime()
  );
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
  metricType: MetricType;
}

const CustomTooltip = ({ active, payload, label, metricType }: CustomTooltipProps) => {
  if (!active || !payload?.length) return null;

  const date = new Date(label as string);
  const formattedDate = date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  return (
    <div className="bg-white border border-[rgba(2,2,3,0.1)] rounded p-3 shadow-lg">
      <p className="font-mono text-xs text-[#020203] opacity-60 mb-2">{formattedDate}</p>
      {payload.map((entry, index) => {
        const providerKey = entry.name.replace('_rate', '').replace('_count', '');
        const provider = providerKey as LlmProvider;
        return (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="font-mono text-xs uppercase tracking-wide">
              {LLM_PROVIDER_LABELS[provider] || providerKey}:
            </span>
            <span className="font-semibold">
              {metricType === 'rate' ? `${entry.value}%` : entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

interface ProviderDetailCardProps {
  summary: ProviderSummary;
}

const ProviderDetailCard = ({ summary }: ProviderDetailCardProps) => {
  return (
    <div className="flex items-center gap-4 p-4 bg-[rgba(2,2,3,0.02)] rounded w-full">
      <img
        src={LLM_PROVIDER_LOGOS[summary.provider]}
        alt=""
        className="w-8 h-8 object-contain"
      />
      <div className="flex-1 flex flex-col gap-1">
        <p className="font-sans font-[450] text-[#020203] text-sm">
          {LLM_PROVIDER_LABELS[summary.provider]}
        </p>
        <p className="font-mono text-xs text-[#020203] opacity-50">
          {summary.totalQueries} queries tested
        </p>
      </div>
      <div className="flex items-center gap-4">
        <div className="flex flex-col items-end">
          <p className="font-condensed text-xl text-[#020203]">{summary.mentionRate}%</p>
          <p className="font-mono text-xs text-[#020203] opacity-50">Mention rate</p>
        </div>
        <div className={`flex items-center justify-center px-2 py-1.5 rounded ${
          summary.trend === 'down' ? 'bg-[rgba(225,73,68,0.1)]' :
          summary.trend === 'up' ? 'bg-[rgba(0,169,68,0.1)]' : 'bg-[rgba(2,2,3,0.04)]'
        }`}>
          <div className="flex items-center gap-1">
            <ArrowDown className={`h-3 w-3 ${
              summary.trend === 'down' ? 'text-[#e14944]' :
              summary.trend === 'up' ? 'text-[#00a944] rotate-180' : 'text-gray-400'
            }`} />
            <span className={`font-mono text-xs ${
              summary.trend === 'down' ? 'text-[#e14944]' :
              summary.trend === 'up' ? 'text-[#00a944]' : 'text-gray-400'
            }`}>
              {Math.abs(summary.trendPercentage)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProviderPerformanceChart = ({ timeline, providerSummaries }: ProviderPerformanceChartProps) => {
  const [metricType, setMetricType] = useState<MetricType>('rate');
  const [selectedProvider, setSelectedProvider] = useState<LlmProvider | null>(null);

  const chartData = useMemo(() => transformTimelineData(timeline, metricType), [timeline, metricType]);

  const handleLegendClick = (provider: LlmProvider) => {
    setSelectedProvider(prev => prev === provider ? null : provider);
  };

  const getLineOpacity = (provider: LlmProvider) => {
    if (selectedProvider === null) return 1;
    return selectedProvider === provider ? 1 : 0.2;
  };

  const selectedSummary = selectedProvider
    ? providerSummaries.find(s => s.provider === selectedProvider)
    : null;

  return (
    <div className="bg-white border border-[rgba(2,2,3,0.06)] flex flex-col gap-4 items-start p-6 rounded w-full">
      <div className="flex items-center justify-between w-full flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-[#020203] opacity-50" />
          <p className="font-mono font-medium leading-[1.5] opacity-50 text-[#020203] text-xs tracking-[0.6px] uppercase">
            Performance by LLM Provider
          </p>
        </div>
        <div className="flex items-center gap-1 bg-[rgba(2,2,3,0.04)] rounded p-1">
          <button
            onClick={() => setMetricType('rate')}
            className={`px-3 py-1.5 rounded text-xs font-mono font-medium uppercase tracking-[0.6px] transition-colors ${
              metricType === 'rate'
                ? 'bg-white text-[#020203] shadow-sm'
                : 'text-[#020203] opacity-50 hover:opacity-70'
            }`}
          >
            Rate %
          </button>
          <button
            onClick={() => setMetricType('count')}
            className={`px-3 py-1.5 rounded text-xs font-mono font-medium uppercase tracking-[0.6px] transition-colors ${
              metricType === 'count'
                ? 'bg-white text-[#020203] shadow-sm'
                : 'text-[#020203] opacity-50 hover:opacity-70'
            }`}
          >
            Count
          </button>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(2,2,3,0.06)" />
          <XAxis
            dataKey="date"
            tickFormatter={formatDate}
            tick={{ fontSize: 11, fill: '#020203', opacity: 0.5 }}
            tickLine={false}
            axisLine={{ stroke: 'rgba(2,2,3,0.1)' }}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#020203', opacity: 0.5 }}
            tickLine={false}
            axisLine={{ stroke: 'rgba(2,2,3,0.1)' }}
            allowDecimals={false}
            tickFormatter={(value) => metricType === 'rate' ? `${value}%` : String(value)}
          />
          <Tooltip content={<CustomTooltip metricType={metricType} />} />
          {ALL_LLM_PROVIDERS.map(provider => (
            <Line
              key={provider}
              type="monotone"
              dataKey={metricType === 'rate' ? `${provider}_rate` : `${provider}_count`}
              name={metricType === 'rate' ? `${provider}_rate` : `${provider}_count`}
              stroke={PROVIDER_COLORS[provider]}
              strokeWidth={selectedProvider === provider ? 3 : 2}
              strokeOpacity={getLineOpacity(provider)}
              dot={{ fill: PROVIDER_COLORS[provider], strokeWidth: 0, r: 3 }}
              activeDot={{ r: 5, fill: PROVIDER_COLORS[provider] }}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>

      <div className="flex flex-wrap justify-center gap-3 w-full pt-2">
        {ALL_LLM_PROVIDERS.map(provider => (
          <button
            key={provider}
            onClick={() => handleLegendClick(provider)}
            className={`flex items-center gap-2 px-2 py-1 rounded transition-all ${
              selectedProvider === provider
                ? 'bg-[rgba(2,2,3,0.06)]'
                : selectedProvider !== null
                  ? 'opacity-40 hover:opacity-70'
                  : 'hover:bg-[rgba(2,2,3,0.04)]'
            }`}
          >
            <img
              src={LLM_PROVIDER_LOGOS[provider]}
              alt=""
              className="w-4 h-4 object-contain"
            />
            <span className="font-mono text-xs uppercase tracking-wide text-[#020203]">
              {LLM_PROVIDER_LABELS[provider]}
            </span>
          </button>
        ))}
      </div>

      {selectedSummary && (
        <ProviderDetailCard summary={selectedSummary} />
      )}
    </div>
  );
};

export default ProviderPerformanceChart;
